"use client";

import { useCallback, useMemo } from "react";
import { AIRDROP_FARMING_SPOT_SUPPORTED_EXCHANGES, CEDE_CEXS, WalletType } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { AIRDROP_FARMING_EXCHANGE_CONFIG, type AirdropFarmingTabId } from "@/constants/airdrop-farming";
import { useApi } from "@/hooks/core/useApi";
import { getWalletTypesForExchange } from "@/hooks/portfolio/portfolioBalanceQuery";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";

type CoinBalances = Record<string, { freeBalance?: number } | undefined>;

/**
 * Parse balance from the backend response per exchange.
 * - Paradex: USDC only
 * - GRVT: USDT first, USDC fallback (pick one, not sum)
 * - Spot CEX (e.g. MEXC): quoteCoin first (e.g. USDT), USDC fallback
 *
 * When walletTypes is passed in the request, the response only contains
 * the requested wallet type — no cross-wallet summing risk.
 */
function parseBalance(
  accountBalances: Record<string, CoinBalances>,
  exchangeId: AirdropFarmingTabId,
  quoteCoin?: string,
): string {
  const configCoin = AIRDROP_FARMING_EXCHANGE_CONFIG[exchangeId]?.settlementCoin;
  const defaultCoins = configCoin ? [configCoin] : [];
  const coins = quoteCoin ? [quoteCoin, ...defaultCoins.filter((c) => c !== quoteCoin)] : defaultCoins;

  for (const coin of coins) {
    let coinTotal = new Decimal(0);
    for (const balances of Object.values(accountBalances)) {
      const balance = balances[coin];
      if (balance?.freeBalance != null) {
        coinTotal = coinTotal.plus(new Decimal(balance.freeBalance));
      }
    }
    if (!coinTotal.isZero()) {
      return coinTotal.toDecimalPlaces(2, Decimal.ROUND_DOWN).toFixed(2);
    }
  }
  return "0";
}

export const EXCHANGE_ACCOUNT_BALANCE_QUERY_KEY = "airdrop-farming-account-balance";

/**
 * Shared useQuery hook for fetching exchange account balance.
 * Keyed by (accountId, exchangeId) so the cache is automatically shared
 * across grid-bot and delta-neutral forms using the same account.
 */
export function useExchangeAccountBalance(
  accountId: number | null | undefined,
  exchangeId: AirdropFarmingTabId,
  quoteCoin?: string,
) {
  const { auth } = useHubUser();
  const apiHelper = useApi();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [EXCHANGE_ACCOUNT_BALANCE_QUERY_KEY, accountId, exchangeId],
    queryFn: async () => {
      const queryParams: Record<string, string> = { accountId: accountId!.toString() };
      if (AIRDROP_FARMING_SPOT_SUPPORTED_EXCHANGES.has(exchangeId)) {
        queryParams.walletTypes = getWalletTypesForExchange(exchangeId).join(",");
      }
      if (exchangeId === CEDE_CEXS.BITGET) {
        queryParams.walletTypes = [WalletType.USD_M_FUTURES].join(",");
      }
      const result = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_BALANCES, {
        queryParams,
      });
      const accountBalances = (result[accountId!.toString()] ?? result[accountId!] ?? {}) as Record<
        string,
        CoinBalances
      >;
      return accountBalances;
    },
    enabled: accountId != null && !!exchangeId,
    staleTime: 30_000, // 30s — balances change slowly
    retry: 1,
  });

  const parsedBalance = useMemo(() => {
    if (!query.data) return "0";
    return parseBalance(query.data, exchangeId, quoteCoin);
  }, [query.data, exchangeId, quoteCoin]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [EXCHANGE_ACCOUNT_BALANCE_QUERY_KEY, accountId, exchangeId] });
  }, [queryClient, accountId, exchangeId]);

  return {
    balance: parsedBalance,
    isFetching: query.isFetching,
    hasFetched: query.isSuccess || query.isError,
    error: query.error,
    refetch: query.refetch,
    invalidate,
  };
}
